import { useState, useEffect, useRef } from 'react';
import { Search, X, Check, ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/**
 * SearchableMultiSelect Component
 * A searchable dropdown with multi-select and tags display
 * 
 * Props:
 * - items: array of {id, name, code?, description?} - all available items
 * - selectedIds: array of selected item IDs
 * - onChange: (selectedIds) => void
 * - placeholder: string
 * - searchPlaceholder: string
 * - emptyMessage: string
 * - maxDisplay: number - max tags to display before showing "+X more"
 * - disabled: boolean
 * - color: string - badge color theme (orange, blue, purple, teal)
 */
const SearchableMultiSelect = ({
  items = [],
  selectedIds = [],
  onChange,
  placeholder = 'Select items...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No items found',
  maxDisplay = 5,
  disabled = false,
  color = 'orange',
  testId
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter items based on search
  const filteredItems = items.filter(item => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      item.name?.toLowerCase().includes(searchLower) ||
      item.code?.toLowerCase().includes(searchLower) ||
      item.sku?.toLowerCase().includes(searchLower) ||
      item.description?.toLowerCase().includes(searchLower)
    );
  });

  // Get selected items
  const selectedItems = items.filter(item => selectedIds.includes(item.id));

  const toggleItem = (itemId) => {
    if (selectedIds.includes(itemId)) {
      onChange(selectedIds.filter(id => id !== itemId));
    } else {
      onChange([...selectedIds, itemId]);
    }
  };

  const removeItem = (itemId, e) => {
    e.stopPropagation();
    onChange(selectedIds.filter(id => id !== itemId));
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const colorClasses = {
    orange: {
      badge: 'bg-orange-100 text-orange-700 hover:bg-orange-200',
      selected: 'bg-orange-100 border-orange-300',
      check: 'text-orange-600'
    },
    blue: {
      badge: 'bg-blue-100 text-blue-700 hover:bg-blue-200',
      selected: 'bg-blue-100 border-blue-300',
      check: 'text-blue-600'
    },
    purple: {
      badge: 'bg-purple-100 text-purple-700 hover:bg-purple-200',
      selected: 'bg-purple-100 border-purple-300',
      check: 'text-purple-600'
    },
    teal: {
      badge: 'bg-teal-100 text-teal-700 hover:bg-teal-200',
      selected: 'bg-teal-100 border-teal-300',
      check: 'text-teal-600'
    },
    pink: {
      badge: 'bg-pink-100 text-pink-700 hover:bg-pink-200',
      selected: 'bg-pink-100 border-pink-300',
      check: 'text-pink-600'
    }
  };

  const colors = colorClasses[color] || colorClasses.orange;

  return (
    <div ref={containerRef} className="relative">
      {/* Selected Tags Display */}
      <div
        className={cn(
          'min-h-[42px] p-2 border rounded-lg cursor-pointer transition-colors',
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:border-gray-400',
          isOpen ? 'border-gray-400 ring-2 ring-gray-200' : 'border-gray-300'
        )}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        data-testid={testId}
      >
        {selectedItems.length === 0 ? (
          <div className="flex items-center justify-between text-gray-500 px-1">
            <span>{placeholder}</span>
            <ChevronDown className={cn('w-4 h-4 transition-transform', isOpen && 'rotate-180')} />
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5 items-center">
            {selectedItems.slice(0, maxDisplay).map(item => (
              <Badge
                key={item.id}
                className={cn('flex items-center gap-1 pr-1', colors.badge)}
              >
                <span className="max-w-[150px] truncate">{item.name}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => removeItem(item.id, e)}
                    className="ml-1 rounded-full p-0.5 hover:bg-black/10"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </Badge>
            ))}
            {selectedItems.length > maxDisplay && (
              <Badge variant="secondary" className="bg-gray-200 text-gray-700">
                +{selectedItems.length - maxDisplay} more
              </Badge>
            )}
            {!disabled && selectedItems.length > 0 && (
              <button
                type="button"
                onClick={clearAll}
                className="ml-auto text-xs text-gray-500 hover:text-gray-700 px-2"
              >
                Clear all
              </button>
            )}
          </div>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                ref={inputRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className="pl-9 h-9 bg-gray-50 border-gray-300"
                autoFocus
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Items List */}
          <div className="max-h-60 overflow-y-auto p-1">
            {filteredItems.length === 0 ? (
              <div className="py-6 text-center text-gray-500 text-sm">
                {emptyMessage}
              </div>
            ) : (
              filteredItems.map(item => {
                const isSelected = selectedIds.includes(item.id);
                return (
                  <div
                    key={item.id}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors',
                      isSelected ? colors.selected : 'hover:bg-gray-100'
                    )}
                    onClick={() => toggleItem(item.id)}
                    data-testid={testId ? `${testId}-option-${item.id}` : undefined}
                  >
                    <div className={cn(
                      'w-5 h-5 rounded border-2 flex items-center justify-center transition-colors',
                      isSelected ? `${colors.check} border-current bg-current/10` : 'border-gray-300'
                    )}>
                      {isSelected && <Check className="w-3 h-3 text-current" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 truncate">{item.name}</p>
                      {(item.code || item.sku) && (
                        <p className="text-xs text-gray-500 truncate">{item.code || item.sku}</p>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          {filteredItems.length > 0 && (
            <div className="px-3 py-2 border-t border-gray-200 text-xs text-gray-500 flex justify-between">
              <span>{selectedIds.length} selected</span>
              <span>{filteredItems.length} items</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableMultiSelect;
